import type {ExchangeClientReceipt, Payment, PaymentActionRequest, PayinRequisites} from "../../types";
import {ApiError} from "../../api";
import {exchangeErrorMessage} from "../../api/admin/errors";
import {messageForCode, messageForCodeWithDetails, payinTermsMessages} from "../../api/error-codes";
import {externalCommandErrorMessage} from "../../api/external-command-errors";
import type {ErrorCode} from "../../generated/openapi-contract";
import {formatAmount, splitEqually, traderPayinTerms} from "../../utils";
import {MAX_RECEIPT_BYTES, isSupportedReceiptFile} from "./types";
import type {AmountAnalysis, ConfirmAction, RateAnalysis, ReceiptDraft} from "./types";
import {WORKFLOW_EXCHANGE_BUY, WORKFLOW_EXCHANGE_SELL, isExternalWorkflow, isPayinWorkflow} from "./workflow";

// Правила, по которым панель решает, можно ли завершить платёж: сумма против
// остатка заявки, лимит платежей, курс и реквизиты приёма. Вынесено из
// компонента отдельным модулем, чтобы эти правила можно было проверить тестами,
// а не только глазами через UI.

/**
 * Причина, по которой файл не годится в чеки; пустая строка — годится.
 *
 * Порядок проверок — тип, потом размер: у файла может не подойти и то и другое,
 * и «не тот формат» точнее объясняет, что делать дальше, чем «слишком большой».
 */
export function rejectFile(file: File): string {
    if (!isSupportedReceiptFile(file)) {
        return `«${file.name}» — неподходящий тип. Принимаются PDF, JPG, PNG и WEBP.`;
    }
    if (file.size > MAX_RECEIPT_BYTES) return `«${file.name}» больше 15 МБ.`;
    return "";
}

export function analyzeAmount(payment: Payment, rawAmount: string): AmountAnalysis {
    const remaining = getRemainingAmount(payment);
    const amount = parseRubToKopeks(rawAmount);
    // Read-model fields from the backend (TraderPaymentItem). maxPayments <= 0
    // means "no known limit": we then don't enforce the last-payment rule on the
    // client and let the backend decide.
    const maxPayments = payment.maxPayments ?? 0;
    const paymentsMade = payment.paymentsMade ?? 0;
    const hasLimit = maxPayments > 0;
    const currentPayment = paymentsMade + 1;
    const valid = amount != null && amount > 0;
    const full = valid && amount === remaining;
    // Частичного закрытия больше нет (UOF-02): заявку закрывает один платёж на
    // весь остаток, и поле суммы этим остатком заполнено и заблокировано.
    // Проверки остаются страховкой на случай, если в поле окажется что-то ещё.
    const overpay = valid && amount > remaining;
    const expectedParts = payinPartsCompletionAmount(payment);
    const ok = valid && (payment.payinParts ? expectedParts !== null && amount === expectedParts : full);

    return {
        remaining,
        amount,
        valid,
        full,
        overpay,
        ok,
        currentPayment,
        maxPayments,
        hasLimit,
    };
}

// Keep the client-side guard aligned with backend validation (>0, <= 10000).
export const MAX_CURRENCY_RATE = 10_000;

export function analyzeRate(rawRate: string): RateAnalysis {
    const compact = rawRate.trim().replace(/\s/g, "").replace(",", ".");
    const precisionOk = compact === "" || /^\d+(?:\.\d{1,2})?$/.test(compact);
    const value = precisionOk && compact !== "" ? Number(compact) : null;
    const inRange = value != null && Number.isFinite(value) && value > 0 && value <= MAX_CURRENCY_RATE;

    return {
        value,
        normalized: inRange ? compact : null,
        valid: inRange,
        precisionOk,
        ok: precisionOk && inRange,
    };
}

export function getRemainingAmount(payment: Payment): number {
    return payment.amountRemaining ?? payment.amount;
}

export function payinPartsCompletionAmount(payment: Payment): number | null {
    const parts = payment.payinParts?.parts;
    if (!parts || parts.length < 2 || parts.some((p) => !p.confirmedAt && !p.failureId)) return null;
    const failed = parts.some((p) => p.failureId);
    if (failed && (payment.claimTimeoutLocked || (payment.hasAppeal && payment.appealReason !== "merchant_payment_failed") || (payment.expiresAt && Date.parse(payment.expiresAt) <= Date.now()))) return null;
    const amount = parts.filter((p) => p.confirmedAt).reduce((sum, p) => sum + p.amount, 0);
    return amount > 0 ? amount : null;
}

export function completionInputAmount(payment: Payment): number {
    return payment.payinParts
        ? payment.payinParts.parts.filter((p) => p.confirmedAt).reduce((sum, p) => sum + p.amount, 0)
        : getRemainingAmount(payment);
}

export function parseRubToKopeks(value: string): number | null {
    const normalized = value.replace(/\s/g, "").replace(",", ".");
    if (!normalized) return null;
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.round(parsed * 100);
}

export function formatRubInput(kopeks: number): string {
    // formatAmount ставит неразрывные пробелы; в поле ввода нужны обычные.
    return formatAmount(kopeks).replace(/ /g, " ");
}

export function getDisabledReason(
    hasReceipt: boolean,
    analysis: AmountAnalysis,
    rate: RateAnalysis,
    isIncome: boolean,
    hasPayinRequisites: boolean,
    fixedRate: string | null,
    payment?: Payment
): string {
    // Закрытая на площадке заявка отвечает первой и одна: остальные подсказки
    // объясняют, чего не хватает для закрытия, а здесь закрывать уже нечего.
    if (payment) {
        if (isTerminalPaymentStatus(payment.status)) return "Платёж уже закрыт. Доступно только чтение подтверждения.";
        const closed = closedAtSourceNotice(payment);
        if (closed) return closed;
        const exchangeMissing = exchangeDataMissingNotice(payment);
        if (exchangeMissing) return exchangeMissing;
    }
    if (isIncome && !hasPayinRequisites) return "Сначала сохраните реквизиты приёма.";
    const portalIn = payment?.merchantPortalIn === true;
    const portalProofReady = payment != null && merchantPortalProofReady(payment);
    // У покупки USDT чек прикладывает клиент, и требование к трейдеру здесь
    // ровно одно — дождаться его файла. Свой чек по такой заявке не нужен, и
    // просить его значило бы обещать проверку, которой у сервера нет.
    if (payment && isExchangeBuyOrder(payment)) {
        const waiting = exchangeCompleteBlockedReason(payment);
        if (waiting) return waiting;
    } else if (portalIn ? !portalProofReady : !hasReceipt) {
        if (payment?.payinParts) return payment.payinParts.parts.some((p) => p.failureId)
            ? "Подтвердите полученные платежи. Остальные части должны иметь доказательство отказа; посторонний спор или истёкший срок требуют администратора."
            : "Проверьте чеки и подтвердите поступление по каждому реквизиту.";
        return portalIn ? "Мерчант должен приложить чек по актуальным реквизитам." : "Сначала приложите чек — он обязателен.";
    }
    if (!analysis.valid) return isIncome ? "Укажите фактически полученную сумму." : "Укажите фактически проведённую сумму.";
    if (analysis.overpay) return "Сумма превышает остаток заявки.";
    if (!analysis.ok) return payment?.payinParts ? "Сумма должна совпадать с подтверждёнными частями." : "Заявка закрывается одним платежом на весь остаток.";
    // У фикс-заявки курса от трейдера не ждём — блокировать по нему нечего.
    if (fixedRate != null) return "";
    if (!rate.precisionOk) return "Курс должен быть числом с максимум двумя знаками после запятой.";
    if (!rate.valid) return "Укажите курс (₽ за 1 USDT), больше 0 и не больше 10000.";
    return "";
}

// exchangeDataMissingNotice — заявка-обмен приехала без второй ноги. Такого
// быть не должно: строка exchange_orders пишется в одной транзакции с заявкой.
// Но если это всё же случилось, трейдеру некуда отправлять USDT, и денежные
// действия по заявке обязаны быть выключены — иначе он закроет рубли по
// заявке, вторую половину которой закрыть нечем.
//
// Пустая строка — либо заявка не обменная вовсе, либо данные на месте.
export function exchangeDataMissingNotice(payment: Payment): string {
    if (payment.workflowKind !== WORKFLOW_EXCHANGE_BUY) return "";
    if (payment.exchange) return "";
    return "Данные обмена недоступны: адреса клиента у заявки нет, закрывать её нельзя.";
}

// isExchangeBuyOrder — заявка-обмен, и только она. Признак — неизменяемый
// сценарий заявки, а не наличие блока exchange: блок пропадает у испорченной
// заявки, а сценарий не меняется никогда, и правила панели обязаны держаться за
// него.
export function isExchangeBuyOrder(payment: Payment): boolean {
    return payment.workflowKind === WORKFLOW_EXCHANGE_BUY;
}

export function isExchangeSellOrder(payment: Payment): boolean {
    return payment.workflowKind === WORKFLOW_EXCHANGE_SELL;
}

// clientReceipts — чеки, приложенные клиентом. Пустой массив у любой заявки,
// кроме обменной, и у обменной, пока клиент ничего не прислал.
export function clientReceipts(payment: Payment): ExchangeClientReceipt[] {
    return payment.exchange?.clientReceipts ?? [];
}

// clientSignalsPresent — по заявке прошёл признак актива со стороны клиента:
// отметка об оплате ИЛИ хотя бы один чек. Тот же вопрос и тот же ответ, что у
// сервера: после любого из них заявку нельзя ни вернуть в пул, ни отменить.
export function clientSignalsPresent(payment: Payment): boolean {
    if (!isExchangeBuyOrder(payment)) return false;
    return Boolean(payment.exchange?.clientMarkedPaidAt) || clientReceipts(payment).length > 0;
}

// clientSignalsBlockedReason — текст, объясняющий, почему обе кнопки отмены
// выключены. Пустая строка — препятствий нет.
export function clientSignalsBlockedReason(payment: Payment): string {
    if (!clientSignalsPresent(payment)) return "";
    return "Клиент уже отметил оплату — доступны только подтверждение и спор.";
}

// exchangeCompleteBlockedReason — почему «Деньги получены» ещё не нажимается.
//
// У покупки чек прикладывает КЛИЕНТ, и до его файла подтверждать нечего: сервер
// откажет тем же условием. Пустая строка — заявка не обменная либо чек уже есть.
export function exchangeCompleteBlockedReason(payment: Payment): string {
    if (!isExchangeBuyOrder(payment)) return "";
    if (clientReceipts(payment).length > 0) return "";
    return "Дождитесь чека клиента.";
}

// --- USDT-этап заявки-обмена ---

// Статус второй ноги, при котором этап закрыт. Строка та же, что в CHECK
// таблицы и в ответе сервера; остальные два состояния (`pending`, `escalated`)
// панель различать не обязана — вопрос у неё один: закрыт этап или нет.
const EXCHANGE_PAYOUT_CONFIRMED = "confirmed";

// TX_HASH_PATTERN — форма хеша транзакции TRON: ровно 64 hex-символа.
// Та же проверка стоит на сервере и решает именно она; здесь она нужна, чтобы
// кнопка не отправляла заведомо отвергаемый запрос.
const TX_HASH_PATTERN = /^[0-9a-fA-F]{64}$/;

export function isValidTxHash(raw: string): boolean {
    return TX_HASH_PATTERN.test(raw.trim());
}

// exchangePayoutOpen — по заявке открыт USDT-этап: рубли закрыты, а перевод
// клиенту ещё не подтверждён.
//
// Условие `status === "success"` здесь несущее, а не уточняющее. Этап
// открывается ТОЛЬКО успехом рублей: до него заявка активна и так, а после
// отмены её USDT-этап не откроется уже никогда. Без этого условия каждая
// отменённая когда-либо заявка-обмен трейдера вечно висела бы у него «в
// работе» — список «мои заявки» отдаёт все его платежи, включая закрытые.
export function exchangePayoutOpen(payment: Payment): boolean {
    if (!isExchangeBuyOrder(payment)) return false;
    if (payment.status !== "success") return false;
    return (payment.exchange?.payoutStatus ?? "") !== EXCHANGE_PAYOUT_CONFIRMED;
}

// isActiveTraderPayment — остаётся ли заявка в работе трейдера.
//
// До покупки USDT ответ был один: пока платёж не терминален. У покупки успех
// рублей не заканчивает работу — открывается вторая нога, и заявка обязана
// остаться на экране, иначе трейдер закрывает рубли и теряет из виду перевод,
// который ещё должен сделать.
//
// Правило живёт здесь, а не в странице, потому что спрашивают его двое:
// фильтр списка и оптимистичное удаление строки после действия. Разойдясь, они
// показали бы заявку в списке и убрали бы её из него одним и тем же кликом.
export function isActiveTraderPayment(payment: Payment): boolean {
    if (!isTerminalPaymentStatus(payment.status)) return true;
    return exchangePayoutOpen(payment) || externalCompletionKeepsTrader(payment);
}

// Команда площадке держит заявку у трейдера, пока она в пути. Завершение, остановленное на
// проверку, — дело админа: трейдеру делать нечего, и заявка уходит в историю. Отмена на
// проверке остаётся: её трейдер доводит руками в Paylord.
function externalCompletionKeepsTrader(payment: Payment): boolean {
    const completion = payment.externalCompletion;
    if (completion == null || completion.state === "confirmed") return false;
    return completion.action === "cancelled" || completion.state !== "attention_required";
}

export function isTerminalPaymentStatus(status: string): boolean {
    return ["success", "cancelled"].includes((status ?? "").toLowerCase());
}

// drawerStaysOpen — остаётся ли карточка открытой после успешного действия.
//
// Вопрос свой, отдельный от isActiveTraderPayment, хотя ответ сегодня один и
// тот же: список решает, показывать ли строку, а карточка — не выдёргивать ли
// её из-под трейдера в момент, когда работа продолжается. Раньше карточка
// закрывалась всегда, и у покупки USDT это была прямая потеря: успех рублей
// открывает второй этап, а трейдер в ту же секунду терял из виду и его, и
// рублёвую часть — реквизиты, отметку клиента и его чеки, скачать которые
// больше неоткуда.
//
// У всех прочих сценариев успех, отмена и спор карточку закрывают ровно как
// закрывали: открытого этапа у них не бывает, и функция для них всегда false.
export function drawerStaysOpen(payment: Payment): boolean {
    return exchangePayoutOpen(payment) || externalCompletionKeepsTrader(payment);
}

// Статус просроченного этапа: окно наблюдения истекло, перевода в сети не
// нашли, и этап передан админу.
const EXCHANGE_PAYOUT_ESCALATED = "escalated";

// payoutSentBlockedReason — почему кнопка «USDT отправлены» не нажимается.
//
// Порядок причин повторяет порядок гейтов сервера: сначала состояние заявки,
// потом состояние этапа, и только потом введённый хеш. Иначе трейдер читал бы
// «проверьте хеш» по заявке, по которой отправлять ещё нечего.
//
// Пустая строка — препятствий нет.
export function payoutSentBlockedReason(payment: Payment, txHash: string): string {
    if (!isExchangeBuyOrder(payment)) return "Этап отправки USDT есть только у заявки-обмена.";
    if (!payment.exchange) return exchangeDataMissingNotice(payment);
    if (payment.status !== "success") return "Сначала подтвердите получение рублей.";
    if (payment.exchange.payoutStatus === EXCHANGE_PAYOUT_CONFIRMED) return "USDT-этап уже закрыт.";
    // Просроченный этап трейдер не закрывает. Сервер отказал бы и так — его
    // запросы принимают только `pending`, — но кнопка, которая ведёт к отказу,
    // хуже выключенной: она обещает действие, которого уже нет.
    if (payment.exchange.payoutStatus === EXCHANGE_PAYOUT_ESCALATED) {
        return "Окно ожидания истекло: этап передан админу.";
    }
    // Отправка уже записана и ждёт подтверждения сети. Второй хеш означал бы
    // второй перевод, и сервер отвечает на него отказом.
    if (payoutSentReported(payment)) return "Отправка записана, ждём подтверждения сети.";
    if (!isValidTxHash(txHash)) return "Вставьте hash транзакции: ровно 64 символа 0-9 и a-f.";
    return "";
}

// payoutSentReported — трейдер уже сказал, что отправил, а этап ещё не закрыт.
//
// Такое состояние существует только при включённом автоподтверждении: там клик
// фиксирует отправку, а закрывает этап сеть. При выключенном клик закрывает
// этап целиком, и между «отправлено» и «подтверждено» ничего не помещается.
export function payoutSentReported(payment: Payment): boolean {
    const exchange = payment.exchange;
    if (!exchange) return false;
    return exchange.payoutStatus !== EXCHANGE_PAYOUT_CONFIRMED && Boolean(exchange.payoutSentAt);
}

// exchangePayoutEscalated — этап просрочен и передан админу.
export function exchangePayoutEscalated(payment: Payment): boolean {
    return payment.exchange?.payoutStatus === EXCHANGE_PAYOUT_ESCALATED;
}

// exchangeWatchRemaining — сколько осталось от окна наблюдения, словами.
//
// Пустая строка означает «показывать нечего»: окна нет, оно уже истекло либо
// этап закрыт. Возвращается именно текст, а не число секунд: считать одно и то
// же дважды — в тесте и в разметке — значит однажды посчитать по-разному.
export function exchangeWatchRemaining(payment: Payment, now: Date = new Date()): string {
    const until = payment.exchange?.watchUntil;
    if (!until) return "";
    if (payment.exchange?.payoutStatus !== "pending") return "";
    const parsed = new Date(until);
    if (Number.isNaN(parsed.getTime())) return "";
    const seconds = Math.floor((parsed.getTime() - now.getTime()) / 1000);
    if (seconds <= 0) return "";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours} ч ${minutes} мин`;
    if (minutes > 0) return `${minutes} мин`;
    return "меньше минуты";
}

// exchangePayoutStatusLabel — состояние этапа человеческими словами.
//
// Источник подтверждения назван вслух: «подтверждено · админ» и «подтверждено ·
// трейдер» — разные факты, и трейдер, увидевший первое по своей заявке, должен
// понять, что этап закрыл не он.
export function exchangePayoutStatusLabel(payment: Payment): string {
    const exchange = payment.exchange;
    if (!exchange) return "";
    if (exchange.payoutStatus !== EXCHANGE_PAYOUT_CONFIRMED) {
        if (exchange.payoutStatus === EXCHANGE_PAYOUT_ESCALATED) return "просрочено, передано админу";
        // «Отправлено» и «подтверждено» — разные факты, и трейдер обязан их
        // различать: первое сказал он сам, второе увидела сеть.
        if (payoutSentReported(payment)) return "отправлено, ждём сеть";
        return "ожидает отправки";
    }
    const source = exchange.payoutConfirmedSource === "admin"
        ? "админ"
        : exchange.payoutConfirmedSource === "chain"
          ? "сеть"
          : "трейдер";
    const at = exchange.payoutConfirmedAt ? ` ${formatTimeOfDay(exchange.payoutConfirmedAt)}` : "";
    return `подтверждено · ${source}${at}`;
}

// formatTimeOfDay — часы и минуты локального времени. Дата у этого события не
// нужна: этап закрывается в тот же день, когда трейдер сидит в панели, а полная
// метка рядом с коротким статусом читается хуже.
function formatTimeOfDay(iso: string): string {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleTimeString(undefined, {hour: "2-digit", minute: "2-digit"});
}

// EXCHANGE_SIGNAL_POLL_MS — как часто перечитывается список «мои заявки», пока
// открыта панель покупки или продажи. Пять секунд — компромисс: сигнал клиента приходит
// снаружи и никакого канала уведомления у нас для него пока нет, а адресный
// WebSocket — отдельная задача. Опрос всего списка дешевле точечной ручки
// ровно потому, что этот список панель и так читает.
export const EXCHANGE_SIGNAL_POLL_MS = 5000;

// shouldPollClientSignals — нужен ли опрос прямо сейчас.
//
// Exchange signals and a merchant's receipt arrive outside the trader panel.
// Poll only these workflows while their relevant state can still change.
export function shouldPollClientSignals(selected: Payment | null): boolean {
    return selected != null && (
        isExchangeBuyOrder(selected) ||
        isExchangeSellOrder(selected) ||
        (selected.merchantPortalIn === true && (selected.status === "pending" || selected.status === "appeal"))
    );
}

export function blankPayinRequisites(): PayinRequisites {
    return {cardNumber: "", cardHolder: "", bankName: ""};
}

/**
 * Реквизиты из `count` частей с суммой поровну. Реквизиты уже набранных частей
 * остаются на своих местах: при смене суммы пересчитываются только деньги.
 */
export function equalPayinParts(count: number, total: number, previous?: PayinRequisites["parts"]): PayinRequisites {
    return {
        ...blankPayinRequisites(),
        parts: splitEqually(total, count).map((amount, index) => ({amount, requisites: previous?.[index]?.requisites ?? blankPayinRequisites()})),
    };
}

export function initialPayinRequisites(payment: Payment): PayinRequisites {
    if (payment.payinRequisites?.parts?.length) return normalizePayinRequisites(payment.payinRequisites);
    // Мерчант назвал, до скольких переводов может оплатить: форма открывается с этим
    // числом реквизитов и суммой поровну, лишние трейдер убирает — вплоть до одного.
    const transferCount = payment.payinRequisites ? null : traderPayinTerms(payment)?.transferCount ?? null;
    if (transferCount !== null) return equalPayinParts(transferCount, getRemainingAmount(payment));
    return {
        kind: payment.payinRequisites?.kind ?? "card",
        cardNumber: payment.payinRequisites?.cardNumber ?? "",
        cardHolder: payment.payinRequisites?.cardHolder ?? "",
        bankName: payment.payinRequisites?.bankName ?? "",
        bankCode: payment.payinRequisites?.bankCode ?? "",
        payment: payment.payinRequisites?.payment ?? "",
        redirect: payment.payinRequisites?.redirect ?? "",
        iframe: payment.payinRequisites?.iframe ?? "",
        iframeLink: payment.payinRequisites?.iframeLink ?? "",
        phone: payment.payinRequisites?.phone ?? "",
        recipient: payment.payinRequisites?.recipient ?? "",
    };
}

export function normalizePayinRequisites(value: PayinRequisites): PayinRequisites {
    if (value.parts?.length) return {
        cardNumber: "", cardHolder: "", bankName: "",
        parts: value.parts.map((part) => ({amount: part.amount, requisites: normalizePayinRequisites(part.requisites)})),
    };
    return {
        kind: value.kind ?? "card",
        cardNumber: value.cardNumber.trim(),
        cardHolder: value.cardHolder.trim(),
        bankName: value.bankName.trim(),
        bankCode: optionalTrim(value.bankCode),
        payment: optionalTrim(value.payment),
        redirect: optionalTrim(value.redirect),
        iframe: optionalTrim(value.iframe),
        iframeLink: optionalTrim(value.iframeLink),
        phone: optionalTrim(value.phone),
        recipient: optionalTrim(value.recipient),
    };
}

export function validatePayinRequisites(value: PayinRequisites, total?: number): boolean {
    if (value.parts?.length) return value.parts.length >= 2 && value.parts.length <= 20 &&
        value.parts.every((part) => Number.isSafeInteger(part.amount) && part.amount > 0 && !part.requisites.parts?.length && validatePayinRequisites(part.requisites)) &&
        (total === undefined || value.parts.reduce((sum, part) => sum + part.amount, 0) === total);
    const normalized = normalizePayinRequisites(value);
    if (normalized.kind === "sbp") return Boolean(normalized.phone && normalized.recipient && normalized.bankName);
    return Boolean(normalized.cardNumber && normalized.cardHolder && normalized.bankName);
}

/** The only client-side exception to the trader receipt rule. */
export function merchantPortalProofReady(payment: Payment): boolean {
    return payment.payinParts ? payinPartsCompletionAmount(payment) !== null : payment.merchantPortalIn === true && payment.merchantPortalReceiptReady === true;
}

function optionalTrim(value?: string | null): string | null {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? null : trimmed;
}

export function directionLabel(direction: Payment["side"]): string {
    return direction === "in" ? "Входящая" : "Исходящая";
}

// buildActionRequest собирает три кликом подтверждаемых действия. Отмена заявки
// сюда не принимается типом, а не проверкой: у неё есть ввод, и запрос без него
// был бы отвергнут сервером — пусть такой вызов не компилируется.
export function buildActionRequest(
    payment: Payment,
    action: Exclude<ConfirmAction, "cancelOrder">,
    amountAnalysis: AmountAnalysis,
    rateAnalysis: RateAnalysis,
    receipts: ReceiptDraft[],
    onReceiptUploaded?: (file: File, receiptId: string) => void
): PaymentActionRequest {
    if (action === "complete") {
        return {
            action: "complete",
            payment,
            receipts: receipts.map((receipt) => ({file: receipt.file, receiptId: receipt.receiptId})),
            onReceiptUploaded,
            // Набор чеков предъявляется на любой заявке (UOF-02): трейдер мог
            // закрыть её несколькими переводами, и тогда чеков несколько на
            // одном платеже. Сервер требует предъявления ровно там же.
            presentReceiptIds: true,
            fiatAmountReal: amountAnalysis.amount ?? 0,
            currencyRate: rateAnalysis.normalized ?? undefined,
        };
    }
    return {action, payment};
}

// buildCancelOrderRequest собирает отмену заявки отдельно от buildActionRequest.
//
// Отдельной функцией, а не пятым аргументом соседке: у этого действия свой
// ввод, который никому больше не нужен, и своя пара полей, которую больше
// никто не заполняет. Загруженные и выбранные чеки при этом едут теми же
// полями, что у закрытия, — отправляет их одна и та же страница одним и тем же
// циклом, и второго способа загрузить файл в системе нет.
//
// Рубли в копейки переводятся здесь, тем же parseRubToKopeks, что и у закрытия:
// умножение на сто живёт ровно в одном месте фронта.
export function buildCancelOrderRequest(
    payment: Payment,
    receipts: ReceiptDraft[],
    requisiteLast4: string,
    rawAmount: string,
    onReceiptUploaded?: (file: File, receiptId: string) => void
): PaymentActionRequest {
    return {
        action: "cancelOrder",
        payment,
        receipts: receipts.map((receipt) => ({file: receipt.file, receiptId: receipt.receiptId})),
        onReceiptUploaded,
        requisiteLast4: requisiteLast4.trim(),
        fiatAmount: parseRubToKopeks(rawAmount) ?? 0,
    };
}

// isExternalOrder reads the immutable workflow selected at creation. Deadlines
// and remote identifiers are data of the workflow, not substitutes for it.
//
// Мультичек на этот признак больше не завязан (UOF-02): несколько чеков на
// одном платеже бывают у любой заявки. Остался один потребитель — предсказание
// отказа «начатую заявку кабинета доводит тот же трейдер».
export function isExternalOrder(payment: Payment): boolean {
    return isExternalWorkflow(payment.workflowKind);
}

// formatActionError maps backend responses to Russian messages for the drawer.
// Refusals the drawer names in its own words carry a `code` (ErrorCode in the
// OpenAPI spec); anything else falls back to a status-based message.
//
// Отдельной ветки «платёж в споре» здесь больше нет, и это не упрощение:
// спор перестал быть статусом платежа и стал меткой заявки, поэтому платежа в
// статусе `appeal` не бывает вовсе. Прежний текст обещал бы трейдеру
// заморозку, которой нет — по спорной заявке действия как раз доступны (В-27),
// а «не в статусе ожидания» теперь значит ровно то, что написано: платёж
// закрыт или бронь истекла.
const NOT_PENDING_MESSAGE =
    "Платёж уже не в статусе ожидания — возможно, бронь истекла или он уже завершён.";
const STAYS_WITH_TRADER_MESSAGE =
    "Заявка уже частично оплачена и остаётся за вами: доведите её до конца или откройте спор.";

const actionErrors: Partial<Record<ErrorCode, string>> = {
    merchant_portal_requisites_issued: "Реквизиты уже выданы мерчанту. Заявка остаётся у текущего трейдера: завершите её или откройте спор.",
    last_payment_must_close_order: "Последний платёж должен закрыть весь остаток заявки.",
    amount_exceeds_remaining: "Сумма превышает остаток заявки.",
    invalid_currency_rate: "Курс должен быть положительным числом не больше 10000.",
    receipt_missing: "К платежу не приложен чек.",
    payin_requisites_missing: "Сначала сохраните реквизиты приёма.",
    payin_requisites_unsupported: "Реквизиты приёма доступны только для заявок на приём.",
    receipt_file_unsupported: "Чек принимается в формате PDF, JPG, PNG или WEBP.",
    // Отдельная строка, а не общее «не тот формат»: бэкенд опознал файл по
    // содержимому и увидел, что имя обещает другое. Трейдеру важно, что
    // переименовать файл недостаточно.
    receipt_file_extension_mismatch: "Содержимое файла не совпадает с его расширением — приложите исходный файл.",
    receipt_file_too_large: "Файл чека превышает лимит 15 МБ.",
    file_required: "Не выбран файл чека.",
    invalid_multipart_form: "Не удалось прочитать файл. Попробуйте другой файл.",
    payment_not_pending: NOT_PENDING_MESSAGE,
    // Начатую заявку кабинета доводит тот же трейдер (В-22): отмены у неё
    // нет, есть спор. Без этой строки отказ показывался бы как «платёж уже
    // не в статусе ожидания» — неправда, платёж на месте и ждёт закрытия.
    external_order_has_paid_parts: STAYS_WITH_TRADER_MESSAGE,
    claim_timeout_appeal_stays_with_trader: STAYS_WITH_TRADER_MESSAGE,
    // Два отказа заявки-обмена: панель называет оставшийся путь, а не
    // пересказывает английский текст сервера.
    client_receipt_required: "Клиент ещё не приложил чек — подтвердить получение нельзя.",
    client_signals_present: "Клиент уже отметил оплату: заявку нельзя вернуть в пул или отменить — подтвердите её или откройте спор.",
    receipt_not_of_payment: "Часть чеков не относится к этому платежу — приложите их заново.",
    receipt_presented_twice: "Один и тот же чек приложен дважды.",
    // Файлов у платежа больше одного, а какими из них он закрыт, знает
    // только трейдер. Молча отправить один из N нельзя.
    presented_receipts_required: "К платежу загружено несколько чеков — отметьте те, которыми закрываете, и повторите.",
    too_many_presented_receipts: "За одно закрытие можно приложить не больше 20 чеков.",
    payment_forbidden: "Этот платёж вам не принадлежит.",
    receipt_forbidden: "Этот платёж вам не принадлежит.",
    payment_not_found: "Платёж не найден.",
};

export function formatActionError(err: unknown): string {
    if (err instanceof ApiError) {
        const known = messageForCodeWithDetails(err, payinTermsMessages) ?? messageForCode(err, actionErrors) ?? externalCommandErrorMessage(err) ?? exchangeErrorMessage(err);
        if (known) return known;

        switch (err.status) {
            case 401:
                return "Требуется повторный вход.";
            case 403:
                return "Этот платёж вам не принадлежит.";
            case 404:
                return "Платёж не найден.";
            case 409:
                return "Состояние заявки изменилось или действие сейчас недоступно. Обновите заявку.";
            default:
                return "Не удалось выполнить действие. Попробуйте ещё раз.";
        }
    }
    if (err instanceof Error) return err.message;
    return "Не удалось выполнить действие. Попробуйте ещё раз.";
}

export function humanSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(2)} МБ`;
}

// closedAtSourceNotice — что показать по заявке, которую площадка закрыла у
// себя, пока трейдер держал её открытой (В-31). Пустая строка — заявка живая.
//
// Решает это НЕ фронт: `externalClosedAtSource` приходит с бэкенда, где список
// смертельных статусов один на шесть мест. Здесь только текст, и `externalStatus`
// в нём — ответ на «чем именно закрыта»: он приезжает словарём площадки и
// переводу не подлежит, потому что искать его человек будет в их кабинете.
export function closedAtSourceNotice(payment: Payment): string {
    if (!payment.externalClosedAtSource) return "";
    const status = payment.externalStatus;
    const what =
        status === "completed"
            ? "закрыта в успех"
            : status === "cancelled"
              ? "отменена"
              : "закрыта";
    return `Заявка ${what} на площадке${status ? ` (${status})` : ""}. `
        + "Действий по ней больше нет — окно можно закрыть.";
}

// isStalledPayment — трейдеру по этой заявке не осталось ни одного действия, и
// на его экране ей место не среди работы, а в отдельном блоке внизу.
//
// Решает это НЕ фронт: `externalStalled` приходит с бэкенда, где обе причины
// («площадка закрыла карточку» и «замок сторожа инцидентов») живут одной
// функцией на весь платёжный слой — той же, что отвечает гейтам взятия и
// подметалке. Копия этого списка здесь разошлась бы с сервером и увела бы вниз
// заявку, по которой сервер ещё принимает действия.
//
// Метка спора признаком не является: спор работу не блокирует (В-27), и заявка
// со спором остаётся в работе у трейдера.
export function isStalledPayment(payment: Payment): boolean {
    return payment.externalStalled;
}

// Reason explains the state; the server lock keeps the merchant rule from
// changing Paylord behavior, where claim_timeout already existed.
export function isClaimTimeoutAppeal(payment: Payment): boolean {
    return payment.appealReason === "claim_timeout" && payment.claimTimeoutLocked;
}

// returnToPoolBlockedReason — почему кнопка «Вернуть в пул» не сработает.
//
// Один случай, когда бэкенд законно откажет: начатую заявку кабинета доводит
// тот же трейдер (ErrExternalOrderHasPaidParts, В-22). Признак заявки кабинета
// у панели тот же, что у мультичека, — срок площадки; признак «начата» —
// paymentsMade > 0. Кнопка с надписью «Вернуть в пул», которая пула не даёт, —
// та же ложь, что и прежнее слово «Отменить», поэтому её выключаем заранее.
//
// Пустая строка — препятствий нет.
export function returnToPoolBlockedReason(payment: Payment): string {
    if (isTerminalPaymentStatus(payment.status)) return "Платёж уже закрыт.";
    if (payment.merchantPortalIn && (payment.payinRequisites || payment.merchantPortalRequisitesVersion || payment.merchantPortalReceiptReady)) {
        return "Реквизиты уже выданы мерчанту — вернуть заявку в пул нельзя. Заявка остаётся у вас.";
    }
    // Сигналы клиента сильнее всех остальных причин: заявку, по которой клиент
    // уже отправил рубли, не отдают следующему трейдеру ни при каком состоянии.
    const signals = clientSignalsBlockedReason(payment);
    if (signals) return signals;
    if (isClaimTimeoutAppeal(payment)) {
        return "Срок истёк, заявка передана в апелляцию и остаётся за вами: завершите её или отмените заявку окончательно.";
    }
    // Закрытую на площадке заявку в пул не вернуть: сервер откажет
    // (ErrExternalOrderClosedAtSource), и следующий трейдер получил бы работу,
    // которую невозможно провести. Проверяется первой — этот отказ сильнее.
    const closed = closedAtSourceNotice(payment);
    if (closed) return closed;
    if (!isExternalOrder(payment)) return "";
    if ((payment.paymentsMade ?? 0) <= 0) return "";
    return "Заявка уже частично оплачена и остаётся за вами: доведите её до конца или откройте спор.";
}

// cancelOrderBlockedReason — почему кнопка «Отменить заявку» не сработает.
//
// Соседка выше отвечает про другое действие и живёт отдельно намеренно:
// «Вернуть в пул» отпускает заявку следующему трейдеру, а эта кнопка её
// убивает, и совпадение двух из трёх причин — совпадение, а не общий предмет.
// Метка спора не блокирует ни ту, ни другую (условие ДМ №1, В-27).
//
// Порядок причин — от неисправимого к исправимому: заявка, закрытую на
// площадке, чинить нечем, оплаченную часть отменой не стирают, а файл трейдер
// приложит прямо здесь.
//
// Пустая строка — препятствий нет.
export function cancelOrderBlockedReason(payment: Payment, chosenReceipts: number): string {
    if (isTerminalPaymentStatus(payment.status)) return "Платёж уже закрыт.";
    // Та же причина и тот же текст, что у соседки: обе двери отмены закрываются
    // одним признаком, и разводить их здесь значило бы обещать трейдеру путь,
    // которого сервер не даёт.
    const signals = clientSignalsBlockedReason(payment);
    if (signals) return signals;
    const closed = closedAtSourceNotice(payment);
    if (closed) return closed;
    if ((payment.paymentsMade ?? 0) > 0) {
        return "По заявке уже прошла оплата — отменить её может только админ. Доведите её до конца или откройте спор.";
    }
    if (payment.merchantPortalIn) {
        return payment.payinRequisites || payment.merchantPortalRequisitesVersion || payment.merchantPortalReceiptReady
            ? "Реквизиты уже выданы мерчанту — отмена доступна только через спор."
            : "";
    }
    // «Загруженный» — уже лежащий у платежа файл ИЛИ выбранный сейчас: выбранные
    // уезжают на сервер той же отправкой, как это делает закрытие. Сервер
    // проверяет уже загруженные строки `receipts`, и к моменту его проверки
    // выбранные ими станут.
    if (chosenReceipts === 0 && !payment.receiptId) {
        return "Прикрепите файл-пруф — без файла отменить нельзя.";
    }
    return "";
}

// cancelOrderRequisiteLast4 — последние 4 цифры реквизита, с которыми сверится
// сервер, или null, если сверять нечего.
//
// null — не «поле пустое», а «поля быть не должно»: у заявки приёма реквизиты
// вводит трейдер и до ввода их нет, а у выплаты по СБП получателя задаёт
// телефон и карты нет вовсе. Сервер в этом случае требует пустого значения и
// подтверждается одной суммой; показать поле значило бы попросить человека
// ввести то, с чем нечему сойтись.
//
// Сами цифры считаются здесь только ради подсказки в модалке — решает сверку
// сервер, и сойтись эти два счёта обязаны: оба берут ПОСЛЕДНИЕ ЧЕТЫРЕ ЦИФРЫ,
// выбросив разделители.
export function cancelOrderRequisiteLast4(payment: Payment): string | null {
    const stored = isPayinWorkflow(payment.workflowKind) ? payment.payinRequisites?.cardNumber : payment.clientCard;
    const digits = (stored ?? "").replace(/\D/g, "");
    if (!digits) return null;
    return digits.slice(-4);
}

// cancelOrderConfirmReady — можно ли нажимать «Отменить заявку» в модалке.
//
// Требования ровно те, что проверит сервер, и не строже: четыре цифры там, где
// реквизит есть, и разбираемая положительная сумма. Сойтись ли им с заявкой —
// не наше дело: ответ на это единственный, и он серверный. Проверять совпадение
// здесь значило бы завести второе место, где система решает, та ли это заявка.
export function cancelOrderConfirmReady(
    requisiteRequired: boolean,
    last4: string,
    rawAmount: string
): boolean {
    if (requisiteRequired && !/^\d{4}$/.test(last4.trim())) return false;
    return parseRubToKopeks(rawAmount) != null;
}

// formatCancelOrderError — русский текст отказа терминальной отмены.
//
// Маркеры сервера, а не проза: три отказа сверки показываются ПРЯМО В МОДАЛКЕ,
// не закрывая её, — трейдер уже ввёл два поля, и выбросить его обратно в
// карточку значило бы сделать защиту от ошибки источником ошибок. Незнакомый
// отказ уходит общему разбору трейдерских действий.
const cancelOrderErrors: Partial<Record<ErrorCode, string>> = {
    merchant_portal_requisites_issued: "Реквизиты уже выданы мерчанту — отмена доступна только через спор.",
    receipt_required: "Отменить заявку без прикреплённого файла нельзя — приложите файл-пруф.",
    requisite_mismatch: "Последние 4 цифры не совпали с реквизитом заявки. Проверьте, ту ли заявку отменяете.",
    amount_mismatch: "Сумма не совпала с суммой заявки. Введите её полностью, до копейки.",
    order_has_paid_parts: "По заявке уже прошла оплата — отменить её может только админ. Доведите её до конца или откройте спор.",
    invalid_requisite_last4: "В поле реквизита нужны ровно 4 цифры.",
    invalid_fiat_amount: "Укажите сумму заявки.",
};

export function formatCancelOrderError(err: unknown): string {
    if (err instanceof ApiError) {
        const known = exchangeErrorMessage(err) ?? messageForCode(err, cancelOrderErrors);
        if (known) return known;
    }
    return formatActionError(err);
}
